import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { themeVar } from '@/styles/theme';
import styled, { css } from 'styled-components';

const tagSizes = {
  sm: css`
    height: 24px;
    font-size: 13px;
    line-height: 18.2px;
    padding: 2px 10px;
    border-radius: 4px;
  `,
  md: css`
    height: 30px;
    font-size: 16px;
    line-height: 24px;
    padding: 2px 20px;
    border-radius: 5px;
  `,
  lg: css`
    height: 34px;
    font-size: 19px;
    line-height: 26.6px;
    padding: 2px 20px;
    border-radius: 6px;
  `
};

const tagStyleTypes = {
  default: css`
    color: ${themeVar('font.weak')};
    background: ${themeVar('layer.2')};
  `,
  valid: css`
    color: #277326;
    background: #efffea;

    svg {
      path {
        fill: #277326;
      }
    }
  `,
  neutral: css`
    color: #ce7d03;
    background: #ffeaca;

    svg {
      path {
        fill: #ce7d03;
      }
    }
  `,
  warning: css`
    color: #991d29;
    background: #ffeaec;

    svg {
      path {
        fill: #991d29;
      }
    }
  `
};

const tagInfoIconColors = {
  valid: '#277326',
  neutral: '#ce7d03',
  warning: '#991d29'
};

interface TagProps {
  title?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'valid' | 'neutral' | 'warning';
  tooltip?: string;
  className?: string;
}

const Tag = ({ title, children, size = 'lg', styleType = 'default', tooltip, className }: TagProps) => {
  return (
    <TagWrapper size={size} styleType={styleType} className={className}>
      {title}
      {children}
      {tooltip && (
        <Tooltip title={tooltip}>
          <InfoCircleIcon
            fill={tagInfoIconColors?.[styleType as keyof typeof tagInfoIconColors] || themeVar('font.disabled')}
            style={{ position: 'relative', top: '3px', marginLeft: '6px' }}
          />
        </Tooltip>
      )}
    </TagWrapper>
  );
};

export default Tag;

const TagWrapper = styled.div<{ size: TagProps['size']; styleType: TagProps['styleType'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 400;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;

  ${({ size }) => tagSizes[size]}
  ${({ styleType }) => tagStyleTypes[styleType]}
`;
